import { useDispatch, useSelector } from "react-redux";
import { useSubmitComputationToApproverMutation, useUpdateComputationApprovalMutation } from "../../api/actuarial/actuarial-api"
import { clearProposalId, clearTypeModal, clearTypeOfProposalStatus, clearUpdateTypeModal } from "../../features/gms-state-and-modal/modal-slice";
import { useToastify } from "../../library/toastify/toast-provider";
import { resetRemarks } from "../../features/actuarial/remark-slice";
import { SubmitComputationToApproverBody } from "../../api/actuarial/acturial-body";
import { resetBasicInformation } from "../../features/gms-proposal-forms/basic-information/basic-information.slice";
import { setCurrentStep } from "../../features/gms-proposal-forms/steps-channel-proposal_as/steps-channel-proposal_as.slice";
import { resetAllRates, resetRates } from "../../features/actuarial/rates-slice";
import { resetProposalForm } from "../../helper/proposal/proposal-reset-form";
import { resetApprovalState, resetApproverForm, selectApproverRates, setIsOpenApproverModal } from "../../features/actuarial/approval/approval-slice";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useComputationApprovalActions = () => {

    const dispatch = useDispatch();
    const [updateApproval, { isLoading: isLoadingUpdateApproval }] = useUpdateComputationApprovalMutation();
    const [submitComputationForApproval, { isLoading: isLoadingSubmitComputationForApproval }] = useSubmitComputationToApproverMutation();
    const { currentProposalId } = useModalAccess();
    const { showToastNotify } = useToastify();
    const { approver, notes, typeOfAction } = useSelector(selectApproverRates);

    const handleUpdateApproval = async () => {
        try {
            if(typeOfAction === null) return
            if (!currentProposalId) { 
                showToastNotify("Proposal ID is required.", "warning"); 
                return; 
            }
            if (typeOfAction === "deny" && !notes.trim()) {
                showToastNotify("Please provide a note before denying.", "warning");
                return;
            }
            const notePayload = notes.trim() ? { notes: notes.trim(), } : {};
            const response =  await updateApproval({ proposalId: currentProposalId, type: typeOfAction, ...notePayload }).unwrap()
            dispatch(clearUpdateTypeModal());
            dispatch(resetBasicInformation());
            dispatch(clearProposalId());
            dispatch(clearTypeOfProposalStatus());
            dispatch(clearTypeModal());
            dispatch(setCurrentStep(1));
            dispatch(resetRates())
            dispatch(resetAllRates())
            dispatch(resetApprovalState())
            resetProposalForm(dispatch);
            dispatch(resetRemarks())
            showToastNotify(response.message, 'success');
        } catch (error: any) {
            const errorMessage = error.errors?.[0]?.msg || error.message || "Unable to update computation approval.";
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleSubmitComputationForApproval = async () => {
        try {
            if (!currentProposalId) {
            showToastNotify("Proposal ID is required.", "error");
            return;
        }
        if (!approver) {
            showToastNotify("Please select an approver before submitting.", "error");
            return;
        }
        const body: SubmitComputationToApproverBody = {
            approver_id: approver.id,
            special_actuarial_notes: notes,
        };

        const response =  await submitComputationForApproval({ proposalId: currentProposalId, body }).unwrap()
        dispatch(resetApproverForm())
        dispatch(setIsOpenApproverModal(false))
        showToastNotify(response.message, 'success');
        } catch (error: any) {
            const errorMessage = error.errors?.[0]?.msg || error.data.message || "Unable to update computation approval.";
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleApproveComputationDetails = async () => {
        try {
            if (!currentProposalId) {
            showToastNotify("Proposal ID is required.", "error");
            return;
        }
        if (!approver) {
            showToastNotify("Please select an approver before submitting.", "error");
            return;
        }
        const body: SubmitComputationToApproverBody = {
            approver_id: approver.id,
            special_actuarial_notes: notes,
        };
        
        const response =  await submitComputationForApproval({ proposalId: currentProposalId, body }).unwrap()
        dispatch(resetApprovalState())
        showToastNotify(response.message, 'success');
        } catch (error: any) {
            const errorMessage = error.errors?.[0]?.msg || error.data.message || "Unable to update computation approval.";
            showToastNotify(errorMessage, 'error');
        }
    }

    return {
        handleUpdateApproval,
        isLoadingUpdateApproval,
        handleSubmitComputationForApproval,
        isLoadingSubmitComputationForApproval,
    }
}